// ============================================================
// THE COMPANY — PromptBuilder Component
// Generates and displays role prompts / context-reset prompts.
// ============================================================

import { useMemo, useState } from 'react';
import Modal from '../shared/Modal';
import CopyButton from '../shared/CopyButton';
import { useCompanyStore } from '../../store/useCompanyStore';
import {
  generateRolePrompt,
  generateContextReset,
} from '../../lib/promptTemplates';

interface PromptBuilderProps {
  roleId: string;
  isOpen: boolean;
  onClose: () => void;
}

type Tab = 'new-chat' | 'context-reset';

export default function PromptBuilder({
  roleId,
  isOpen,
  onClose,
}: PromptBuilderProps) {
  const [activeTab, setActiveTab] = useState<Tab>('new-chat');

  const roles = useCompanyStore((s) => s.roles);
  const decisions = useCompanyStore((s) => s.decisions);

  const role = useMemo(() => roles.find((r) => r.id === roleId), [roles, roleId]);

  const informedDecisions = useMemo(
    () => decisions.filter((d) => d.informedRoleIds.includes(roleId)),
    [decisions, roleId],
  );

  const newChatPrompt = useMemo(() => {
    if (!role) return '';
    return generateRolePrompt(role, roles, informedDecisions);
  }, [role, roles, informedDecisions]);

  const contextResetPrompt = useMemo(() => {
    if (!role) return '';
    const summary = role.notes || '(No session summary available — paste one into this role\'s notes first.)';
    return generateContextReset(role, roles, informedDecisions, summary);
  }, [role, roles, informedDecisions]);

  const activePrompt = activeTab === 'new-chat' ? newChatPrompt : contextResetPrompt;

  if (!role) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Prompt — ${role.name}`}
      width="640px"
    >
      {/* Tabs */}
      <div
        className="flex gap-1 rounded-lg p-1 mb-4"
        style={{ backgroundColor: 'var(--bg-elevated)' }}
      >
        <TabButton
          active={activeTab === 'new-chat'}
          onClick={() => setActiveTab('new-chat')}
          label="New Chat Prompt"
        />
        <TabButton
          active={activeTab === 'context-reset'}
          onClick={() => setActiveTab('context-reset')}
          label="Context Reset Prompt"
        />
      </div>

      {/* Prompt preview */}
      <div
        className="rounded-lg overflow-auto max-h-[60vh] p-4 text-sm font-mono leading-relaxed whitespace-pre-wrap"
        style={{
          backgroundColor: 'var(--bg-base)',
          border: '1px solid var(--border-subtle)',
          color: 'var(--text-secondary)',
        }}
      >
        {activePrompt}
      </div>

      {/* Copy button */}
      <div className="flex justify-end mt-4">
        <CopyButton
          text={activePrompt}
          label={activeTab === 'new-chat' ? 'Copy Prompt' : 'Copy Reset Prompt'}
        />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Internal tab button
// ---------------------------------------------------------------------------

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        flex-1 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150
        ${
          active
            ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-sm'
            : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
        }
      `}
    >
      {label}
    </button>
  );
}
